import { useMemo, useState } from 'react'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { z, type ZodSchema, useForm } from '@sevenrooms/form'
import { useLocales, getCountryData, type CountryCode } from '@sevenrooms/locales'
import { DateOnly, TimeOnly } from '@sevenrooms/timepiece'
import type { SelectOption } from '@sevenrooms/ui-kit/core'
import {
  Form,
  FormTimeRangePicker,
  Label,
  TextArea,
  type TimeRangePickerForm,
  useTimeRangePickerForm,
  Button,
  FormSelect,
  Checkbox,
} from '@sevenrooms/ui-kit/form'
import { useMaxWidthBreakpoint } from '@sevenrooms/ui-kit/hooks'
import { Icon } from '@sevenrooms/ui-kit/icons'
import { VStack, HStack, DividerLine, Grid, Modal, ModalBody, ModalFooter, ModalTitle } from '@sevenrooms/ui-kit/layout'
import { Text } from '@sevenrooms/ui-kit/typography'
import { AgreementDetailModal } from '../../../components/AgreementTerms/AgreementTerm'
import { EmailAddressForm } from '../../../components/EmailAddressForm/EmailAddressForm'
import { FirstNameForm } from '../../../components/FirstNameForm/FirstNameForm'
import { LastNameForm } from '../../../components/LastNameForm/LastNameForm'
import { PhoneNumberForm } from '../../../components/PhoneNumberForm/PhoneNumberForm'
import { useCreateRequestMutation } from '../../../domain/request'
import { useVenue, useVenueGroup, useWidgetSettings } from '../../../hooks'
import { reservationWidgetMessages } from '../../../reservationWidgetMessages'
import { useReservationFormState, useModals } from '../../../store'
import { requestSubmitted } from '../../../utils'
import { BackModalHeader } from '../BackModalHeader'

export type CreateRequestForm = ZodSchema<typeof useCreateRequestFormSchema>
export const useCreateRequestFormSchema = () => {
  const { formatMessage } = useLocales()
  const { startOfDayTime } = useVenue()
  const widgetSettings = useWidgetSettings()
  const timeRange = useTimeRangePickerForm({
    startOfDayTime,
    nullable: widgetSettings.formatOfRequestingTime === 'single',
    allowSameTime: true,
  })
  const requiredErrorMessage = formatMessage(reservationWidgetMessages.resWidgetErrorsFieldRequired)
  const invalidErrorMessage = formatMessage(reservationWidgetMessages.resWidgetErrorsFieldInvalid)

  return useMemo(
    () =>
      z
        .object({
          emailAddress: z.string().min(1, { message: requiredErrorMessage }).email({ message: invalidErrorMessage }),
          firstName: z.string().min(1, { message: requiredErrorMessage }),
          lastName: z.string().min(1, { message: requiredErrorMessage }),
          notes: z.string(),
          phoneCountryCode: z.custom<CountryCode>(),
          phoneDialCode: z.string().min(1),
          phoneNumber: z.string().min(1, { message: requiredErrorMessage }),
          startAndEndTime: z.string().nullable(),
          timeRange,
          agreedVenueSpecificMarketingOnForRequest: z.boolean(),
          agreedVenueGroupMarketingOnForRequest: z.boolean(),
          agreedTailoredCommunicationOnForRequest: z.boolean(),
          agreedVenueSmsMarketingOnForRequest: z.boolean(),
        })
        .superRefine((val, ctx) => {
          if (!isPossiblePhoneNumber(val.phoneNumber)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: invalidErrorMessage,
              path: ['phoneNumber'],
            })
          }
          if (widgetSettings.formatOfRequestingTime === 'single' && !val.startAndEndTime) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: requiredErrorMessage,
              path: ['startAndEndTime'],
            })
          }
        }),
    [invalidErrorMessage, requiredErrorMessage, timeRange, widgetSettings.formatOfRequestingTime]
  )
}

export interface CreateRequestProps {
  validRequestTimes: string[]
  partySize: number
  startDate: string
  onClose: () => void
  onBack?: () => void
}

export function CreateRequest({ onClose, onBack, validRequestTimes, partySize, startDate }: CreateRequestProps) {
  const { id: venueId, urlKey, name: venueName } = useVenue()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { isSmsMarketingEnabled } = useVenue()
  const { formatMessage } = useLocales()
  const isMobile = useMaxWidthBreakpoint('s')
  const [createRequest, { isLoading }] = useCreateRequestMutation({
    fixedCacheKey: 'reservation-create-request',
  })
  const widgetSettings = useWidgetSettings()
  const { lockedFields } = useVenueGroup()
  const { formState: createReservationFormState, updateFormState } = useReservationFormState()
  const { showModal, showErrorModal } = useModals()

  const phoneDialCode = getCountryData(createReservationFormState.phoneCountryCode)?.dialCode ?? '1'
  const requestFormSchema = useCreateRequestFormSchema()
  const {
    selectedExperience,
    emailAddress,
    firstName,
    lastName,
    notes,
    phoneCountryCode,
    phoneNumber,
    startTime,
    preferredLanguage,
    trackingSlug,
    selectedOtherVenue,
  } = createReservationFormState

  const {
    formatOfRequestingTime,
    venueSpecificMarketingOnForRequest,
    isVenueSpecificMarketingPolicyDefaultOnForRequest,
    venueGroupMarketingOnForRequest,
    isVenueGroupMarketingPolicyDefaultOnForRequest,
    tailoredCommunicationOnForRequest,
    isTailoredCommunicationPolicyDefaultOnForRequest,
    venueSmsMarketingOnForRequest,
    isVenueSmsMarketingPolicyDefaultOnForRequest,
  } = selectedOtherVenue || widgetSettings

  const timeRange: TimeRangePickerForm =
    startTime === ReservationWidget.AllTimesOption
      ? {
          startTime: null,
          endTime: null,
        }
      : {
          startTime: validRequestTimes[0] || null,
          endTime: validRequestTimes[validRequestTimes.length - 1] || null,
        }
  const agreedVenueSpecificMarketingOnForRequest = venueSpecificMarketingOnForRequest
    ? isVenueSpecificMarketingPolicyDefaultOnForRequest
    : false
  const agreedVenueGroupMarketingOnForRequest = venueGroupMarketingOnForRequest ? isVenueGroupMarketingPolicyDefaultOnForRequest : false
  const agreedTailoredCommunicationOnForRequest = tailoredCommunicationOnForRequest
    ? isTailoredCommunicationPolicyDefaultOnForRequest
    : false
  const agreedVenueSmsMarketingOnForRequest =
    isSmsMarketingEnabled && venueSmsMarketingOnForRequest ? isVenueSmsMarketingPolicyDefaultOnForRequest : false
  const form = useForm(requestFormSchema, {
    mode: 'onSubmit',
    defaultValues: {
      emailAddress,
      firstName,
      lastName,
      notes,
      phoneCountryCode,
      phoneDialCode,
      phoneNumber,
      startAndEndTime: null,
      timeRange,
      agreedVenueSpecificMarketingOnForRequest,
      agreedVenueGroupMarketingOnForRequest,
      agreedTailoredCommunicationOnForRequest,
      agreedVenueSmsMarketingOnForRequest,
    },
  })
  const { field, getValues, setError } = form
  const disabled = isSubmitting || isLoading

  const saveOnClose = () => {
    updateFormState({ ...createReservationFormState, ...getValues() })
    onClose()
  }

  const onSubmit = async (data: CreateRequestForm) => {
    try {
      setIsSubmitting(true)
      updateFormState({ ...createReservationFormState, ...data })
      const timeRange = data.timeRange as { startTime: string; endTime: string }
      await createRequest({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.emailAddress,
        phoneNumber: data.phoneNumber,
        countryCode: data.phoneCountryCode,
        phoneDialCode: data.phoneDialCode,
        date: startDate,
        startTime: formatOfRequestingTime === 'single' && data.startAndEndTime ? data.startAndEndTime : timeRange.startTime,
        endTime: formatOfRequestingTime === 'single' ? null : timeRange.endTime,
        partySize,
        preferredLanguageCode: preferredLanguage,
        requestClass: 'table',
        clientRequest: data.notes,
        requestSmsOptIn: false,
        venueId: selectedExperience?.venueId || venueId,
        // eslint-disable-next-line no-warning-comments
        // TODO GX-2914
        channel: 'SEVENROOMS_WIDGET',
        trackingSlug,
        clientId: undefined,
        experienceId: selectedExperience?.id,
        referralId: undefined,
        venueSpecificMarketingOptIn: venueSpecificMarketingOnForRequest ? data.agreedVenueSpecificMarketingOnForRequest : undefined,
        venueGroupMarketingOptIn: venueGroupMarketingOnForRequest ? data.agreedVenueGroupMarketingOnForRequest : undefined,
        tailoredCommunicationOptIn: tailoredCommunicationOnForRequest ? data.agreedTailoredCommunicationOnForRequest : undefined,
        venueSmsMarketingOptIn: venueSmsMarketingOnForRequest ? data.agreedVenueSmsMarketingOnForRequest : undefined,
      }).unwrap()
      showModal('createRequestSuccess')
      requestSubmitted(urlKey, startDate, partySize, timeRange.startTime, timeRange.endTime)
    } catch (error) {
      if (typeof error === 'string') {
        setError('timeRange', {
          message: formatMessage(reservationWidgetMessages.resWidgetRequestsAlreadyExistsError),
        })
      } else {
        showErrorModal()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInvalid = (data: unknown) => {
    // Remove before mvp, useful for debugging for now.
    // eslint-disable-next-line no-console
    console.log(data)
  }
  const timeOptions: SelectOption<string>[] = validRequestTimes.map(tm => ({ id: tm, label: TimeOnly.from(tm).formatSTime() }))
  const formattedDate = DateOnly.fromSafe(startDate)?.formatNYearFMonthNDayFWeek()
  const isRequestForExactTimeEnabled = formatOfRequestingTime === 'single'
  return (
    <Form onSubmit={onSubmit} onInvalid={handleInvalid} {...form}>
      <Modal
        data-test="sr-create-request-modal"
        width="100%"
        ariaLabel={formatMessage(reservationWidgetMessages.resWidgetRequestsSubmitRequestTitle)}
      >
        <BackModalHeader onClose={saveOnClose} onBack={onBack}>
          <VStack spacing="s">
            <ModalTitle
              data-test="sr-create-request-modal-title"
              title={formatMessage(reservationWidgetMessages.resWidgetRequestDetailsLabel)}
            />
          </VStack>
        </BackModalHeader>
        <ModalBody>
          <VStack mb="l" mt="m" spacing="m">
            <HStack spacing="s" alignItems="center">
              <Icon name="GX-calendar-range" size="lg" />
              <Text data-test="sr-create-request-modal-date">{formattedDate}</Text>
            </HStack>
            <HStack spacing="s" alignItems="center">
              <Icon name="GX-account-multiple" size="lg" />
              <Text data-test="sr-create-request-modal-party-size">
                {`${partySize} ${
                  partySize === 1
                    ? formatMessage(reservationWidgetMessages.commonGuestLabel)
                    : formatMessage(reservationWidgetMessages.commonGuestsLabel)
                }`}
              </Text>
            </HStack>
          </VStack>
          <VStack spacing="m">
            {isRequestForExactTimeEnabled ? (
              <Label primary={`${formatMessage(reservationWidgetMessages.resWidgetTimePickerLabel)}*`}>
                <FormSelect
                  field={field.prop('startAndEndTime')}
                  options={timeOptions}
                  withEmpty
                  hideEmptyOption
                  searchable={false}
                  withNativeSupport
                />
              </Label>
            ) : (
              <Label primary={`${formatMessage(reservationWidgetMessages.resWidgetPriorityAlertsReservationTimeBetween)}*`}>
                <FormTimeRangePicker
                  data-test="sr-create-request-modal-time-range"
                  field={field.prop('timeRange')}
                  disabled={disabled}
                  timeSlots={validRequestTimes}
                  selectProps={{ withNativeSupport: true, zIndexLayer: 'modal' }}
                />
              </Label>
            )}
            <Label primary={formatMessage(reservationWidgetMessages.resWidgetReservationNotesHeader)}>
              <TextArea fullWidth resize="none" field={field.prop('notes')} disabled={disabled} />
            </Label>
            <DividerLine ml="none" mr="none" />
            <Grid
              gridAutoColumns="auto auto"
              gridTemplateColumns={`repeat(${isMobile ? 1 : 2}, minmax(0, 1fr))`}
              gap="m"
              alignItems="start"
            >
              <FirstNameForm firstNameField={field.prop('firstName')} disabled={lockedFields?.firstName || disabled} />
              <LastNameForm lastNameField={field.prop('lastName')} disabled={lockedFields?.lastName || disabled} />
              <EmailAddressForm emailAddressField={field.prop('emailAddress')} disabled={lockedFields?.email || disabled} />
              <PhoneNumberForm
                phoneNumberField={field.prop('phoneNumber')}
                disabled={lockedFields?.phoneNumber || disabled}
                phoneCountryCodeField={field.prop('phoneCountryCode')}
                phoneDialCodeField={field.prop('phoneDialCode')}
              />
            </Grid>
            {isSmsMarketingEnabled && venueSmsMarketingOnForRequest && (
              <Checkbox
                field={field.prop('agreedVenueSmsMarketingOnForRequest')}
                data-test="agreedVenueSmsMarketingOnForRequest"
                checked={isVenueSmsMarketingPolicyDefaultOnForRequest}
                extra={
                  <AgreementDetailModal
                    title={formatMessage(reservationWidgetMessages.resWidgetMarketingPolicyHeader)}
                    content={formatMessage(
                      reservationWidgetMessages.policyVenueSpecificSmsMarketing,
                      { venue: venueName },
                      { ignoreTag: true }
                    )}
                  />
                }
              >
                {formatMessage(reservationWidgetMessages.resWidgetCheckoutVenueSmsOptIn, { venue: venueName })}
              </Checkbox>
            )}
            {venueSpecificMarketingOnForRequest && (
              <Checkbox
                field={field.prop('agreedVenueSpecificMarketingOnForRequest')}
                data-test="agreedVenueSpecificMarketingOnForRequest"
                checked={isVenueSpecificMarketingPolicyDefaultOnForRequest}
                extra={
                  <AgreementDetailModal
                    title={formatMessage(reservationWidgetMessages.resWidgetMarketingPolicyHeader)}
                    content={formatMessage(
                      reservationWidgetMessages.policyVenueSpecificMarketing,
                      { venue: venueName },
                      { ignoreTag: true }
                    )}
                  />
                }
              >
                {formatMessage(reservationWidgetMessages.resWidgetCheckoutVenueOptIn, { venue: venueName })}
              </Checkbox>
            )}
            {venueGroupMarketingOnForRequest && (
              <Checkbox
                field={field.prop('agreedVenueGroupMarketingOnForRequest')}
                data-test="agreedVenueGroupMarketingOnForRequest"
                checked={isVenueGroupMarketingPolicyDefaultOnForRequest}
                extra={
                  <AgreementDetailModal
                    title={formatMessage(reservationWidgetMessages.resWidgetMarketingPolicyHeader)}
                    content={formatMessage(reservationWidgetMessages.policyVenueGroupMarketing, { venue: venueName }, { ignoreTag: true })}
                  />
                }
              >
                {formatMessage(reservationWidgetMessages.resWidgetCheckoutVenueGroupOptIn)}
              </Checkbox>
            )}
            {tailoredCommunicationOnForRequest && (
              <Checkbox
                field={field.prop('agreedTailoredCommunicationOnForRequest')}
                data-test="agreedTailoredCommunicationOnForRequest"
                checked={isTailoredCommunicationPolicyDefaultOnForRequest}
              >
                {formatMessage(reservationWidgetMessages.textTailoredCommunicationOptInLabel)}
              </Checkbox>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            data-test="submit-a-request-button"
            disabled={disabled}
            data-test-id="sr-create-request-modal-set-alert"
            type="submit"
            fullWidth
            size="l"
          >
            {formatMessage(reservationWidgetMessages.resWidgetSubmitRequestButton)}
          </Button>
        </ModalFooter>
      </Modal>
    </Form>
  )
}
